import React from "react";
import classNames from "classnames";
import { Button } from "../button";
import { Image } from "../image";
import { FormattedMessage } from "react-intl";
import styles from "./styles.modules.scss";
import { Pressable } from "../pressable";
import { Loader } from "../loader";
import { useParams } from "react-router-dom";
import { usePollContext } from "../../stores/poll";
import { useSnowplowContext } from "../../analytics/snowplow";

export type PollOption = {
    id: string;
    title: string;
    image: ImageSourceSets;
};

export type PollCommonProps = {
    pollId: string;
    pollTitle: string;
    options: PollOption[];
    pollTargets?: [];
    targeting?: Targeting;
};

type OptionWithResult = PollOption & {
    result: number | undefined;
    isLeading: boolean;
    pollTotalVotes?: number;
    maxBan?: number;
};

export type PollProps = {
    userVote: string[] | null;
    pollTitle: string;
    options: OptionWithResult[];
    showResults: boolean;
    isClosed: boolean;
    canVote: boolean;
    isError: boolean;
    isVoting: boolean;
    vote: (optionIds: string[]) => Promise<[void, void]>;
    pollRef?: React.RefObject<HTMLDivElement>;
    extraClassNames?: string[];
    style?: { [key: string]: string | number };
    onClose?: () => void;
    pollTargets?: [];
    pollDescription?: string;
    targeting?: Targeting;
    campaignId?: string;
};

export type PollType = (props: PollProps) => JSX.Element | null;

const useRandomOrder = (pollId: string) => {
    const [order, setOrder] = React.useState<number[]>([]);

    React.useEffect(() => {
        const generateRandomOrder = () => {
            const options = Array.from({ length: 32 }, (_, index) => index + 1);
            return options.sort(() => Math.random() - 0.5);
        };

        const order = generateRandomOrder();
        setOrder(order);
    }, [pollId]);

    return order;
};

export const usePollResults = (pollId: string, options: PollOption[]) => {
    const {
        state: { voteCache, polls },
    } = usePollContext();
    const order = useRandomOrder(pollId);

    const pollResults = polls[pollId]?.results;

    const optionsWithResults = options
        .map((option) => {
            const optionResult = pollResults?.find(
                (result, index) => option.id === String(index + 1),
            );

            return {
                ...option,
                result: optionResult,
                isLeading:
                    optionResult && pollResults
                        ? pollResults.every((x) => x <= optionResult)
                        : false,
                pollTotalVotes: polls[pollId]?.pollTotalVotes,
            };
        })
        .sort((a, b) =>
            (order[Number(a.id) - 1] ?? 1) < (order[Number(b.id) - 1] ?? 1)
                ? -1
                : 1,
        )
        .filter((x) => x.image);

    const optionsTotalPercentage =
        pollResults?.reduce((v, acc) => acc + v, 0) ?? 0;
    const showResults =
        (polls[pollId]?.status === "closed" && optionsTotalPercentage > 0) ||
        Boolean(
            voteCache.votes[pollId] &&
                pollResults &&
                pollResults.length > 0 &&
                optionsTotalPercentage > 0,
        );

    return { optionsWithResults, showResults };
};

export const useVoteWithAnalytics = (
    pollId: string,
    options: PollOption[],
    vote: (optionIds: string[]) => Promise<[void, void]>,
) => {
    const { trackButtonClick } = useSnowplowContext();

    return async (optionIds: string[]) => {
        const result = await vote(optionIds);

        optionIds.forEach((optionId) => {
            const option = options.find((x) => x.id === optionId);
            const title = option?.title ?? optionId;
            trackButtonClick(`poll-${pollId}-vote-${optionId}`, title);
        });

        return result;
    };
};

function transformPollTargets(
    pollTargets: {
        id: string;
        name: string;
        imageURL: string;
        maxBan: number;
        pollTotalVotes: number;
    }[],
) {
    return pollTargets.map(
        (
            target: {
                id: string;
                name: string;
                imageURL: string;
                maxBan: number;
                pollTotalVotes: number;
            },
            index: number,
        ) => ({
            id: (index + 1).toString(),
            title: target.name,
            image: {
                small: {
                    title: target.name,
                    path: target.imageURL,
                    width: 60,
                    height: 80,
                },
                smallRetina: {
                    title: target.name,
                    path: target.imageURL,
                    width: 120,
                    height: 160,
                },
                medium: {
                    title: target.name,
                    path: target.imageURL,
                    width: 150,
                    height: 200,
                },
                mediumRetina: {
                    title: target.name,
                    path: target.imageURL,
                    width: 180,
                    height: 240,
                },
                large: {
                    title: target.name,
                    path: target.imageURL,
                    width: 240,
                    height: 320,
                },
                largeRetina: {
                    title: target.name,
                    path: target.imageURL,
                    width: 300,
                    height: 400,
                },
            },
            maxBan: target.maxBan,
            isLeading: false,
            result: undefined,
            pollTotalVotes: target.pollTotalVotes,
        }),
    );
}

export const Poll: PollType = ({
    pollTitle,
    options,
    userVote,
    canVote,
    isClosed,
    showResults,
    isError,
    isVoting,
    vote,
    pollRef,
    style,
    onClose,
    extraClassNames,
    pollTargets,
    pollDescription,
    targeting,
    campaignId,
}) => {
    const [selected, setSelected] = React.useState<string[]>([]);

    const params = useParams();
    const locale = params?.lang ? { lang: params.lang } : { lang: "en" };

    const hasVoted = userVote !== null;

    const onVote = () => {
        if (canVote && selected.length > 0) {
            vote(selected).then(() => setSelected([]));
        }
    };

    const showVoteButton = canVote && selected.length > 0;
    const showCloseButton = !showVoteButton;
    const sortOptionsByResult = isClosed && showResults;

    const aspectRatio = window.innerWidth / window.innerHeight;

    let optionsMax = 1;

    if (pollTargets && pollTargets.length > 0) {
        options = transformPollTargets(pollTargets);
        if (options[0]?.maxBan) {
            optionsMax = options[0]?.maxBan;
        }
    }

    // We always want to show the winning option first if the poll is closed
    const sortedOptions = sortOptionsByResult
        ? [
              ...options.sort((a, b) => {
                  return (b.result ?? 0) - (a.result ?? 0);
              }),
          ]
        : options;

    const handleOptionClick = (optionId: string) => {
        if (hasVoted || !canVote) return;

        setSelected((prevSelected) => {
            if (prevSelected.includes(optionId)) {
                return prevSelected.filter((id) => id !== optionId);
            } else if (pollTargets && prevSelected.length < optionsMax) {
                return [...prevSelected, optionId];
            } else if (!pollTargets) {
                return [optionId];
            } else {
                return prevSelected;
            }
        });
    };

    return (
        <div
            className={classNames([
                "poll-view",
                styles.container,
                {
                    "is-closed": isClosed,
                    "is-open": !isClosed,
                    [`${styles.pollTargets}`]: pollTargets,
                    massPoll: options.length > 5,
                },
                `lang-${locale.lang}`,
                targeting?.campaignId ?? "",
                ...(extraClassNames ?? []),
            ])}
            style={style}
            ref={pollRef}
        >
            <div
                className={classNames([
                    "poll-view-content",
                    styles["poll-view-content"],
                    styles.content,
                    { tablet: aspectRatio > 0.666 },
                    {
                        "is-closed": isClosed,
                        "is-open": !isClosed,
                    },
                ])}
            >
                <h1
                    className={classNames([
                        "title",
                        styles.title,
                        {
                            short: pollTitle.length < 40,
                        },
                    ])}
                >
                    {isClosed && pollTargets ? (
                        <FormattedMessage id={"globalText.votingHasEnded"} />
                    ) : hasVoted ? (
                        campaignId === "world" ? (
                            pollTitle
                        ) : (
                            <FormattedMessage
                                id={"globalText.thanksForVoting"}
                            />
                        )
                    ) : (
                        pollTitle
                    )}
                </h1>

                {pollTargets && (
                    <h2>
                        {isClosed ? (
                            <FormattedMessage
                                id={"globalText.votingHasEndedDescription"}
                            />
                        ) : pollDescription && !hasVoted ? (
                            pollDescription
                        ) : (
                            ""
                        )}
                    </h2>
                )}

                <div
                    className={classNames(
                        "optionsContainer",
                        styles.optionsContainer,
                    )}
                >
                    <div
                        className={classNames([
                            "options",
                            styles.options,
                            `options-${options.length}`,
                            styles[`options-${options.length}`],
                            {
                                "show-results": showResults,
                            },
                        ])}
                    >
                        {sortedOptions.map((option, idx) => {
                            const voteResult = option.result ?? 1;

                            const formatPercentage = (
                                value: number,
                            ): string => {
                                const percentage = value * 100;

                                // Check if the decimal part is zero and choose format accordingly
                                return percentage % 1 === 0
                                    ? `${Math.round(percentage)}%`
                                    : `${percentage.toFixed(0)}%`;
                            };

                            const results = formatPercentage(voteResult);

                            const isLastItem = idx === sortedOptions.length - 1;

                            const optionContent = (
                                <div
                                    className={classNames([
                                        "option",
                                        styles.option,
                                        {
                                            voted: userVote?.includes(
                                                option.id,
                                            ),
                                            [String(styles.voted)]:
                                                userVote?.includes(option.id),
                                            unvoted:
                                                (hasVoted &&
                                                    !userVote.includes(
                                                        option.id,
                                                    )) ||
                                                (isClosed &&
                                                    !userVote?.includes(
                                                        option.id,
                                                    )),
                                            selected: selected.includes(
                                                option.id,
                                            ),
                                            [String(styles.selected)]:
                                                selected.includes(option.id),
                                            unselected:
                                                canVote &&
                                                !hasVoted &&
                                                !selected.includes(option.id),
                                            [String(styles.unselected)]:
                                                canVote &&
                                                !hasVoted &&
                                                !selected.includes(option.id),
                                            leading: option.isLeading,
                                            losing:
                                                !option.isLeading &&
                                                showResults,
                                        },
                                    ])}
                                    onClick={() => handleOptionClick(option.id)}
                                >
                                    <div className="image-wrapper">
                                        <Image sources={option.image} />

                                        <div
                                            className={classNames(
                                                "image-wrapper-results",
                                                styles["image-wrapper-results"],
                                                {
                                                    show: showResults,
                                                    [String(styles.show)]:
                                                        showResults,
                                                },
                                            )}
                                        >
                                            {results}
                                        </div>
                                    </div>

                                    <div
                                        className={classNames([
                                            "option-title-results",
                                            styles.optionTitleResults,
                                        ])}
                                    >
                                        {option.title && (
                                            <span
                                                className={classNames([
                                                    styles.optionTitle,
                                                    "option-title",
                                                ])}
                                            >
                                                {option.title}
                                            </span>
                                        )}

                                        <span
                                            className={classNames([
                                                "option-results",
                                                styles.optionResults,
                                                {
                                                    "no-title": !option.title,
                                                    [String(styles.noTitle)]:
                                                        !option.title,
                                                    show: showResults,
                                                    [String(styles.show)]:
                                                        showResults,
                                                },
                                            ])}
                                        >
                                            {results}
                                        </span>
                                    </div>
                                </div>
                            );

                            return (
                                <React.Fragment key={option.id}>
                                    <Pressable
                                        animationSize="large"
                                        disabled={!canVote}
                                    >
                                        {optionContent}
                                    </Pressable>

                                    {!isLastItem && (
                                        <div
                                            className={classNames([
                                                styles.break,
                                                "break",
                                            ])}
                                        ></div>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                {isError && (
                    <div className={classNames(["error", styles.error])}>
                        <FormattedMessage id="globalText.langVoteTryAgainLabel" />
                    </div>
                )}

                <div
                    className={classNames(
                        "poll-action",
                        styles["poll-action"],
                        styles.buttonContainer,
                        {
                            "vote-button":
                                (showVoteButton && !pollTargets) ||
                                (pollTargets && selected.length === optionsMax),
                            "close-button": showCloseButton || pollTargets,
                            disabled: isVoting,
                        },
                    )}
                >
                    {pollTargets ? (
                        <div
                            className={classNames(styles.closeButton, {
                                show: showVoteButton,
                            })}
                        >
                            <Button onClick={onVote} disabled={isVoting}>
                                <span className="buttonTextWrapper">
                                    {selected.length < optionsMax ? (
                                        <>
                                            <FormattedMessage
                                                id={
                                                    "globalText.langVoteDefaultButtonLabel"
                                                }
                                            />
                                            <span>
                                                {" "}
                                                {selected.length}/{optionsMax}
                                            </span>
                                        </>
                                    ) : selected.length === optionsMax ? (
                                        <>
                                            <FormattedMessage
                                                id={
                                                    "globalText.langVoteSubmitLabel"
                                                }
                                            />
                                            <span>
                                                {" "}
                                                {selected.length}/{optionsMax}
                                            </span>
                                        </>
                                    ) : (
                                        <FormattedMessage
                                            id={
                                                "globalText.langVoteSubmitLabel"
                                            }
                                        />
                                    )}
                                </span>
                            </Button>
                        </div>
                    ) : (
                        <div
                            className={classNames(
                                "voteButton",
                                styles.voteButton,
                                {
                                    show: showVoteButton,
                                },
                            )}
                        >
                            <Button onClick={onVote} disabled={isVoting}>
                                <span className="buttonTextWrapper">
                                    <FormattedMessage
                                        id={"globalText.langVoteSubmitLabel"}
                                    />
                                </span>
                            </Button>
                        </div>
                    )}
                    {onClose && (
                        <div
                            className={classNames(
                                "closeButton",
                                styles.closeButton,
                                {
                                    show: showCloseButton,
                                },
                            )}
                        >
                            <Button onClick={onClose}>
                                <span className="buttonTextWrapper">
                                    <FormattedMessage id={"globalText.close"} />
                                </span>
                            </Button>
                        </div>
                    )}

                    {campaignId === "world" && (hasVoted || isClosed) && (
                        <h1>
                            <FormattedMessage
                                id={
                                    isClosed
                                        ? "globalText.votingHasEnded"
                                        : "globalText.thanksForVoting"
                                }
                            />
                        </h1>
                    )}
                </div>
            </div>
        </div>
    );
};

type PollSkeletonProps = {
    extraClassNames?: PollProps["extraClassNames"];
    style?: PollProps["style"];
};

type PollSkeletonType = (props: PollSkeletonProps) => JSX.Element | null;
export const PollSkeleton: PollSkeletonType = ({ style, extraClassNames }) => {
    const aspectRatio = window.innerWidth / window.innerHeight;

    return (
        <div
            className={classNames([
                "poll-view",
                styles.container,
                ...(extraClassNames ?? []),
            ])}
            style={style}
        >
            <div
                className={classNames([
                    "poll-view-content loading",
                    styles.content,
                    styles.loading,
                    { tablet: aspectRatio > 0.666 },
                ])}
            >
                <div className="loader-container">
                    <Loader />
                </div>
            </div>
        </div>
    );
};
